import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Save } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BUSINESS_FIELDS = [
  { key: 'annual_turnover', label: 'Annual Turnover ($)', type: 'number' },
  { key: 'net_profit', label: 'Net Profit ($)', type: 'number' },
  { key: 'director_wages', label: 'Director Wages ($)', type: 'number' },
  { key: 'other_business_income', label: 'Other Business Income ($)', type: 'number' },
  { key: 'gst_registered', label: 'GST Registered', type: 'boolean' },
  { key: 'fy_last_year_turnover', label: 'FY Last Year Turnover ($)', type: 'number' },
  { key: 'fy_last_year_profit', label: 'FY Last Year Profit ($)', type: 'number' },
  { key: 'fy_prev_year_turnover', label: 'FY Previous Year Turnover ($)', type: 'number' },
  { key: 'fy_prev_year_profit', label: 'FY Previous Year Profit ($)', type: 'number' },
  { key: 'ytd_income', label: 'YTD Income ($)', type: 'number' },
];

const APPLICANT_INCOME_FIELDS = [
  { key: 'income_type', label: 'Income Type', type: 'text' },
  { key: 'after_tax_income', label: 'After Tax Income ($)', type: 'number' },
  { key: 'income_frequency', label: 'Frequency', type: 'select', options: ['Weekly', 'Fortnightly', 'Monthly'] },
  { key: 'employer', label: 'Employer', type: 'text' },
  { key: 'time_working', label: 'Time Working', type: 'text' },
  { key: 'second_job_type', label: 'Second Job - Type', type: 'text' },
  { key: 'second_job_address', label: 'Second Job - Address', type: 'text', wide: true },
  { key: 'second_job_occupation', label: 'Second Job - Occupation', type: 'text' },
  { key: 'previous_employment_type', label: 'Previous Employment - Type', type: 'text' },
  { key: 'previous_employment_address', label: 'Previous Employment - Address', type: 'text', wide: true },
  { key: 'previous_employment_occupation', label: 'Previous Employment - Occupation', type: 'text' },
];

const PERSONAL_FIELDS = [
  { key: 'salary_wages', label: 'Salary / Wages ($)', type: 'number' },
  { key: 'rental_income', label: 'Rental Income ($)', type: 'number' },
  { key: 'dividends_distributions', label: 'Dividends / Distributions ($)', type: 'number' },
  { key: 'other_income', label: 'Other Income ($)', type: 'number' },
  { key: 'spouse_income', label: 'Spouse Income ($)', type: 'number' },
];

const HOUSEHOLD_FIELDS = [
  { key: 'dependants', label: 'Dependants', type: 'int' },
  { key: 'basic_living_expenses_per_month', label: 'Basic Living Expenses Per Month ($)', type: 'number' },
  { key: 'childcare_child_maintenance_per_month', label: 'Childcare / Child Maintenance Per Month ($)', type: 'number' },
  { key: 'mobile_internet_pay_tv_per_month', label: 'Mobile / Internet / Pay TV Per Month ($)', type: 'number' },
  { key: 'insurance_expenses_per_month', label: 'Insurance Expenses Per Month ($)', type: 'number' },
  { key: 'fuel_travel_transport_per_month', label: 'Fuel / Travel / Transport Per Month ($)', type: 'number' },
  { key: 'other_expenses_per_month', label: 'Other Expenses Per Month ($)', type: 'number' },
  { key: 'other_expense_type', label: 'Other Expense Type', type: 'text', wide: true },
  { key: 'monthly_living_expenses', label: 'Legacy: Monthly Living Expenses ($)', type: 'number' },
  { key: 'child_support', label: 'Legacy: Child Support ($)', type: 'number' },
  { key: 'other_commitments', label: 'Legacy: Other Commitments ($)', type: 'number' },
];

const emptyForm = {
  business_income: {},
  applicant_1_income: {},
  applicant_2_income: {},
  personal_income: {},
  household_expenses: {},
};

const SECTION_CONFIGS = [
  { title: 'Business Income', key: 'business_income', schema: BUSINESS_FIELDS },
  { title: 'Applicant 1 Income / Employment', key: 'applicant_1_income', schema: APPLICANT_INCOME_FIELDS },
  { title: 'Applicant 2 Income / Employment', key: 'applicant_2_income', schema: APPLICANT_INCOME_FIELDS },
  { title: 'Personal Income', key: 'personal_income', schema: PERSONAL_FIELDS },
  { title: 'Household / Living Expenses', key: 'household_expenses', schema: HOUSEHOLD_FIELDS },
];

export default function IncomeTab({ dealId, onRefresh }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchIncome = async () => {
      try {
        const res = await axios.get(`${API}/crm/deals/${dealId}/income`);
        setForm({
          business_income: res.data?.business_income || {},
          applicant_1_income: res.data?.applicant_1_income || {},
          applicant_2_income: res.data?.applicant_2_income || {},
          personal_income: res.data?.personal_income || {},
          household_expenses: res.data?.household_expenses || {},
        });
      } catch (error) {
        toast.error('Failed to load income data');
      } finally {
        setLoading(false);
      }
    };

    fetchIncome();
  }, [dealId]);

  const updateField = (section, key, value) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: value,
      },
    }));
  };

  const normalizeSection = (data, schema) => {
    return schema.reduce((acc, field) => {
      const val = data?.[field.key];
      if (field.type === 'number') {
        acc[field.key] = val === '' || val === null || val === undefined ? null : Number(val);
      } else if (field.type === 'int') {
        acc[field.key] = val === '' || val === null || val === undefined ? null : parseInt(val, 10);
      } else if (field.type === 'select') {
        acc[field.key] = val || null;
      } else if (field.type === 'boolean') {
        if (val === '' || val === null || val === undefined) acc[field.key] = null;
        else acc[field.key] = val === true || val === 'true';
      } else {
        acc[field.key] = val || null;
      }
      return acc;
    }, {});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        business_income: normalizeSection(form.business_income, BUSINESS_FIELDS),
        applicant_1_income: normalizeSection(form.applicant_1_income, APPLICANT_INCOME_FIELDS),
        applicant_2_income: normalizeSection(form.applicant_2_income, APPLICANT_INCOME_FIELDS),
        personal_income: normalizeSection(form.personal_income, PERSONAL_FIELDS),
        household_expenses: normalizeSection(form.household_expenses, HOUSEHOLD_FIELDS),
      };
      await axios.put(`${API}/crm/deals/${dealId}/income`, payload);
      toast.success('Income and living expenses saved');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save income data');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 text-sm" data-testid="income-tab-loading">Loading income data...</div>;
  }

  const renderField = (sectionKey, field) => {
    const value = form?.[sectionKey]?.[field.key] ?? '';
    const testId = `income-${sectionKey}-${field.key}`;

    if (field.type === 'boolean') {
      return (
        <select
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => updateField(sectionKey, field.key, e.target.value)}
          className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none"
          data-testid={testId}
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (field.type === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => updateField(sectionKey, field.key, e.target.value)}
          className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none"
          data-testid={testId}
        >
          <option value="">Select...</option>
          {field.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type === 'number' || field.type === 'int' ? 'number' : 'text'}
        value={value}
        onChange={(e) => updateField(sectionKey, field.key, e.target.value)}
        className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none"
        data-testid={testId}
      />
    );
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="income-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="income-tab-title">
          <Landmark className="h-5 w-5 text-emerald-400" /> Income & Household Profile
        </h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="save-income-button"
        >
          <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Income'}
        </Button>
      </div>

      {SECTION_CONFIGS.map((section) => (
        <div
          key={section.key}
          className="bg-navy-800/50 border border-white/10 rounded-xl p-4 space-y-3"
          data-testid={`income-section-${section.key}`}
        >
          <h3 className="text-sm text-gray-200 font-semibold" data-testid={`income-section-title-${section.key}`}>
            {section.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.schema.map((field) => (
              <div key={field.key} className={field.wide ? 'md:col-span-2' : ''}>
                <label className="block text-xs text-gray-400 mb-1">{field.label}</label>
                {renderField(section.key, field)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}